/**
 * MobileHeader — top-of-screen header strip for portal / admin
 * layouts below the `md` breakpoint.
 *
 * Renders only on mobile (< 768px). Contains:
 *   1. The active page title on the left (driven by the current
 *      route via `pageTitleForPath`). Replaces the previous brand
 *      wordmark per spec-owner direction (PR (c2)) — the brand is
 *      already implicit in the address bar / app icon, but the
 *      page title is genuinely useful when the desktop
 *      `<PageHeader>` scrolls out of view.
 *   2. A NotificationBell.
 *   3. A hamburger button on the right that opens the sidebar
 *      drawer.
 *
 * The mockup at `reignara_redesign/app/dashboard/page.tsx` is the
 * binding reference: a `flex md:hidden` strip with
 * `border-b border-[#1e1a17]/10 bg-white px-4 py-3`.
 *
 * Spec: meridian-redesign — PR (b2) (drawer pattern), PR (c2)
 *   (page-title swap + notification bell positioning).
 * Requirements: 14.9 (mobile sidebar UX).
 */

import { Menu } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { NotificationBell } from '@/components/ui/NotificationBell';
import { pageTitleForPath } from './pageTitleMap';

export interface MobileHeaderProps {
  /** Called when the user taps the hamburger to open the sidebar. */
  onOpenSidebar: () => void;
  /**
   * Suppress the inline NotificationBell. Used by surfaces (Podium /
   * MessagesPage) that already render their own bell in the workspace
   * chrome and don't want the mobile header to double up.
   */
  hideNotificationBell?: boolean;
}

export function MobileHeader({
  onOpenSidebar,
  hideNotificationBell = false,
}: MobileHeaderProps): JSX.Element {
  const location = useLocation();
  const title = pageTitleForPath(location.pathname);

  return (
    <header
      data-testid="mobile-header"
      className="md:hidden flex items-center justify-between border-b border-border bg-card px-4 py-3 sticky top-0 z-30 gap-3"
    >
      <h1
        data-testid="mobile-header-title"
        className="font-display text-page-title text-foreground not-italic truncate min-w-0"
      >
        {title}
      </h1>
      <div className="flex items-center gap-2 shrink-0">
        {!hideNotificationBell && <NotificationBell />}
        <button
          type="button"
          onClick={onOpenSidebar}
          aria-label="Open navigation"
          data-testid="mobile-header-menu-button"
          className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted text-foreground hover:bg-muted/80 transition-colors touch-target border-none cursor-pointer"
        >
          <Menu className="h-5 w-5" />
        </button>
      </div>
    </header>
  );
}

export default MobileHeader;
